import React, { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useLang } from "../../i18n";

/**
 * Subtle "swipe for more" affordance shown only on small screens.
 * Sits under a horizontally scrollable mockup.
 */
export const ScrollHint = ({ className = "" }) => {
  const { lang } = useLang();
  return (
    <div
      aria-hidden="true"
      data-testid="mock-scroll-hint"
      className={`mt-3 flex items-center justify-center gap-1.5 text-[11px] font-medium text-[var(--slate)] lg:hidden ${className}`}
    >
      <ArrowRight className="h-3.5 w-3.5 animate-swipe-x text-[var(--blue)]" strokeWidth={2} />
      {lang === "de" ? "Wischen für mehr" : "Swipe for more"}
    </div>
  );
};

/**
 * Wraps a product mockup so it keeps full desktop detail on mobile via
 * horizontal scrolling (with a swipe hint), while behaving normally on lg+.
 */
export const MockScroll = ({ children, className = "" }) => (
  <div>
    <div
      className={`mock-scroll -mx-4 overflow-x-auto px-4 py-1 sm:mx-0 sm:px-0 lg:overflow-visible ${className}`}
    >
      {children}
    </div>
    <ScrollHint />
  </div>
);

const EASE_OUT = [0.22, 1, 0.36, 1];

export const Reveal = ({ children, delay = 0, y = 12, className = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.15, margin: "-60px" });
  return (
    <motion.div ref={ref} className={className}
      initial={{ opacity: 0, y }} animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay, ease: EASE_OUT }}>
      {children}
    </motion.div>
  );
};

export const Eyebrow = ({ children }) => (
  <span className="inline-flex items-center gap-2 rounded-full border border-[var(--border)] bg-white px-3 py-1 text-xs font-semibold uppercase tracking-[0.08em] text-[var(--blue)]">
    <span className="h-1.5 w-1.5 rounded-full bg-[var(--blue)]" />
    {children}
  </span>
);

export const Chip = ({ children }) => (
  <span className="inline-flex items-center rounded-full border border-[var(--border)] bg-white px-3 py-1 text-xs font-medium text-[var(--slate)]">
    {children}
  </span>
);

/** Centered marketing heading with a blue accent line. */
export const SectionHeading = ({ eyebrow, title, accent, subline, className = "" }) => (
  <div className={`mx-auto max-w-3xl text-center ${className}`}>
    {eyebrow && <div className="mb-5 flex justify-center"><Eyebrow>{eyebrow}</Eyebrow></div>}
    <h2 className="font-heading text-[1.65rem] font-extrabold leading-[1.14] tracking-tight text-[var(--ink)] [hyphens:auto] [text-wrap:balance] sm:text-[2.1rem] lg:text-[2.6rem]">
      {title} {accent && <span className="text-[var(--blue)]">{accent}</span>}
    </h2>
    {subline && <p className="mx-auto mt-4 max-w-2xl text-[15px] text-[var(--slate)] sm:text-base lg:text-lg">{subline}</p>}
  </div>
);

export const WindowChrome = ({ label }) => (
  <div className="flex items-center gap-2 border-b border-[var(--border)] bg-[var(--surface-2)] px-4 py-2.5">
    <span className="h-2.5 w-2.5 rounded-full" style={{ background: "#ff5f57" }} />
    <span className="h-2.5 w-2.5 rounded-full" style={{ background: "#febc2e" }} />
    <span className="h-2.5 w-2.5 rounded-full" style={{ background: "#28c840" }} />
    {label ? <span className="ml-3 truncate font-mono text-[11px] text-[var(--slate)]/70">{label}</span> : null}
  </div>
);

/** Fit-score ring (conic). Default blue; pass color for variants. */
export const ScoreRing = ({ value = 92, size = 56, label, color = "var(--blue)" }) => {
  const deg = Math.round((value / 100) * 360);
  return (
    <div className="relative grid place-items-center rounded-full"
      style={{ width: size, height: size, background: `conic-gradient(${color} ${deg}deg, #e6ecf5 ${deg}deg)` }}
      aria-label={`${label || "Score"}: ${value}`}>
      <div className="grid place-items-center rounded-full bg-white" style={{ width: size - 10, height: size - 10 }}>
        <span className="font-heading text-sm font-bold text-[var(--ink)]">{value}</span>
      </div>
    </div>
  );
};
